import { ApprovalMode } from "../approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
} from "../ClientTools";

import { RemoveFromPanelConfirmation } from "./confirmations";
import {
  isStringArray,
  MAX_MESSAGE_PATHS_PER_CALL,
  runPanelMutation,
  visualizerStatus,
} from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface RemoveFromPanelInput {
  panel_id: string;
  message_path_ids: string[];
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

function execute(
  input: RemoveFromPanelInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!input.panel_id) {
    return toolError("panel_id is required");
  }
  if (!input.message_path_ids || input.message_path_ids.length === 0) {
    return toolError("message_path_ids is required");
  }
  if (!isStringArray(input.message_path_ids)) {
    return toolError(
      "message_path_ids must be an array of message path id strings",
    );
  }
  if (input.message_path_ids.length > MAX_MESSAGE_PATHS_PER_CALL) {
    return toolError(
      `At most ${MAX_MESSAGE_PATHS_PER_CALL} message paths may be removed in one call.`,
    );
  }

  // Not `isOnVisualizerPage()`: inside a batch that closure answers the
  // pre-batch route, so it still reads true for a page a batch-mate is
  // replacing. Publishing into that workspace would report success against
  // panels that are about to be torn down.
  if (!deps?.publishWorkspaceAction || visualizerStatus(deps) !== "open") {
    return toolError(
      "The visualizer is not open, so there are no panels to modify. " +
        "Use client_visualize_open_panel to open data first.",
    );
  }

  return runPanelMutation(
    deps.publishWorkspaceAction,
    "remove_from_panel",
    (onResult) => ({
      type: "remove_from_panel",
      payload: {
        panelId: input.panel_id,
        messagePathIds: input.message_path_ids,
        onResult,
      },
    }),
  );
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<RemoveFromPanelInput>({
  name: "client_visualize_remove_from_panel",
  description: `Remove one or more message paths from an existing visualizer panel — e.g. take the z series off a plot while keeping x and y. The panel is referenced by its id from the visualizer state context. On a map panel, removing either member of a latitude/longitude pair removes the whole path. Only works while the user is on the visualizer page. On failure nothing is removed and the error lists a per-path reason.`,
  input_schema: {
    type: "object",
    properties: {
      panel_id: {
        type: "string",
        description:
          "Id of the target panel. Panel ids are visible in the visualizer state context.",
      },
      message_path_ids: {
        type: "array",
        items: { type: "string" },
        minItems: 1,
        maxItems: MAX_MESSAGE_PATHS_PER_CALL,
        description:
          "Message path IDs (e.g., ['mp_abc123']) to remove. Each must currently be on the panel.",
      },
    },
    required: ["panel_id", "message_path_ids"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Remove from Panel",
  displayDescription: "Removes data from an existing visualizer panel.",
  ConfirmationContent: RemoveFromPanelConfirmation,
});
